import { ROUTER_API } from 'app/constants/api.constant';
import BaseApi from 'app/services/api/base.api';
import { STORES_PATH, UBERALL_SERVICES_PATH, USE_MOCK } from '../constants';
import {
  KeywordRankingsPayload,
  StoreServiceAreaPayload,
  UberallAiPostBody,
  UberallAiPostPayload,
  UberallAiReplyPayload,
  UberallAiSettingsPayload,
  UberallAnalyticsPayload,
  UberallAutoResponseRuleBody,
  UberallAutoResponseRuleListPayload,
  UberallDataPointPayload,
  UberallDataPointsPagePayload,
  UberallKeywordsPayload,
  UberallListingsSummaryPayload,
  UberallLocationProfilePayload,
  UberallPhotoSlot,
  UberallPhotosPayload,
  UberallPostDirectoriesPayload,
  UberallReplyTemplatePayload,
  UberallScorePayload,
  UberallSeoReportSentPayload,
  UberallSocialConnectionsPayload,
  UberallSocialPlatform,
  UberallSocialPostBody,
  UberallSocialPostListPayload,
  UberallStoreLinkPayload,
} from './contract';
import {
  mockAiPost,
  mockAiReply,
  mockAiSettings,
  mockAnalytics,
  mockAutoResponseRules,
  mockCreateAutoResponseRule,
  mockCreatePost,
  mockCreateReplyTemplate,
  mockCreateServiceArea,
  mockDatapoint,
  mockDatapoints,
  mockDeleteAutoResponseRule,
  mockDeletePhoto,
  mockDeletePost,
  mockDeleteReplyTemplate,
  mockDeleteServiceArea,
  mockDisconnectSocial,
  mockDownloadSeoReport,
  mocked,
  mockGenerateSeoReport,
  mockKeywordRankings,
  mockKeywords,
  mockListings,
  mockLocationProfile,
  mockMarkDatapointRead,
  mockPatchAiSettings,
  mockPatchLocation,
  mockPhotos,
  mockPostDirectories,
  mockPosts,
  mockPutKeywords,
  mockReplyTemplates,
  mockReplyToDatapoint,
  mockScore,
  mockSendSeoReport,
  mockServiceAreas,
  mockSocialConnections,
  mockSocialConnectUrl,
  mockStoreLink,
  mockSyncListings,
  mockUpdateAutoResponseRule,
  mockUpdatePost,
  mockUpdateReplyTemplate,
  mockUpdateServiceArea,
  mockUploadPhoto,
  mockUploadPostVisual,
} from './mocks';

export const visibilityApi = new BaseApi({ baseURL: ROUTER_API, tokenKey: 'token', timeout: 60000 });

const locationPath = (locationId: number, suffix = '') => `${UBERALL_SERVICES_PATH}/locations/${locationId}${suffix}`;

const datapointPath = (datapointId: number, suffix = '') =>
  `${UBERALL_SERVICES_PATH}/datapoints/${datapointId}${suffix}`;

const storePath = (storeId: string, suffix = '') => `${STORES_PATH}/${storeId}${suffix}`;

export const getStoreUberallLinkApi = async (storeId: string) => {
  if (USE_MOCK) return mocked(mockStoreLink);
  const { data } = await visibilityApi.api.get<UberallStoreLinkPayload>(storePath(storeId, '/uberall'));
  return data;
};

export const getAiSettingsApi = async (storeId: string) => {
  if (USE_MOCK) return mocked(mockAiSettings);
  const { data } = await visibilityApi.api.get<UberallAiSettingsPayload>(storePath(storeId, '/uberall/ai'));
  return data;
};

export const patchAiSettingsApi = async (storeId: string, patch: Partial<UberallAiSettingsPayload>) => {
  if (USE_MOCK) return mocked(() => mockPatchAiSettings(patch));
  const { data } = await visibilityApi.api.patch<UberallAiSettingsPayload>(storePath(storeId, '/uberall/ai'), patch);
  return data;
};

export const getReplyTemplatesApi = async (storeId: string) => {
  if (USE_MOCK) return mocked(mockReplyTemplates);
  const { data } = await visibilityApi.api.get<UberallReplyTemplatePayload[]>(
    storePath(storeId, '/uberall/reply-templates'),
  );
  return data;
};

export interface ReplyTemplateBody {
  emoji?: string | null;
  label: string;
  text: string;
  minRating?: number | null;
  maxRating?: number | null;
  theme?: string | null;
}

export const createReplyTemplateApi = async (storeId: string, body: ReplyTemplateBody) => {
  if (USE_MOCK) return mocked(() => mockCreateReplyTemplate(body));
  const { data } = await visibilityApi.api.post<UberallReplyTemplatePayload>(
    storePath(storeId, '/uberall/reply-templates'),
    body,
  );
  return data;
};

export const updateReplyTemplateApi = async (
  storeId: string,
  templateId: string,
  patch: Partial<ReplyTemplateBody>,
) => {
  if (USE_MOCK) return mocked(() => mockUpdateReplyTemplate(templateId, patch));
  const { data } = await visibilityApi.api.patch<UberallReplyTemplatePayload>(
    storePath(storeId, `/uberall/reply-templates/${templateId}`),
    patch,
  );
  return data;
};

export const deleteReplyTemplateApi = async (storeId: string, templateId: string) => {
  if (USE_MOCK) return mocked(() => mockDeleteReplyTemplate(templateId));
  await visibilityApi.api.delete(storePath(storeId, `/uberall/reply-templates/${templateId}`));
};

export const getStoreServiceAreasApi = async (storeId: string) => {
  if (USE_MOCK) return mocked(mockServiceAreas);
  const { data } = await visibilityApi.api.get<StoreServiceAreaPayload[]>(storePath(storeId, '/uberall/service-areas'));
  return data;
};

export interface ServiceAreaBody {
  name: string;
  placeId?: string | null;
}

export const createServiceAreaApi = async (storeId: string, body: ServiceAreaBody) => {
  if (USE_MOCK) return mocked(() => mockCreateServiceArea(body));
  const { data } = await visibilityApi.api.post<StoreServiceAreaPayload>(
    storePath(storeId, '/uberall/service-areas'),
    body,
  );
  return data;
};

export const updateServiceAreaApi = async (storeId: string, areaId: string, patch: Partial<ServiceAreaBody>) => {
  if (USE_MOCK) return mocked(() => mockUpdateServiceArea(areaId, patch));
  const { data } = await visibilityApi.api.patch<StoreServiceAreaPayload>(
    storePath(storeId, `/uberall/service-areas/${areaId}`),
    patch,
  );
  return data;
};

export const deleteServiceAreaApi = async (storeId: string, areaId: string) => {
  if (USE_MOCK) return mocked(() => mockDeleteServiceArea(areaId));
  await visibilityApi.api.delete(storePath(storeId, `/uberall/service-areas/${areaId}`));
};

export const getAutoResponseRulesApi = async (locationId: number) => {
  if (USE_MOCK) return mocked(mockAutoResponseRules);
  const { data } = await visibilityApi.api.get<UberallAutoResponseRuleListPayload>(
    locationPath(locationId, '/auto-response-rules'),
  );
  return data;
};

export const createAutoResponseRuleApi = async (locationId: number, body: UberallAutoResponseRuleBody) => {
  if (USE_MOCK) return mocked(() => mockCreateAutoResponseRule(body), 500);
  const { data } = await visibilityApi.api.post<UberallAutoResponseRuleListPayload>(
    locationPath(locationId, '/auto-response-rules'),
    body,
  );
  return data;
};

export const updateAutoResponseRuleApi = async (
  locationId: number,
  ruleId: string,
  body: UberallAutoResponseRuleBody,
) => {
  if (USE_MOCK) return mocked(() => mockUpdateAutoResponseRule(ruleId, body), 500);
  const { data } = await visibilityApi.api.patch<UberallAutoResponseRuleListPayload>(
    locationPath(locationId, `/auto-response-rules/${ruleId}`),
    body,
  );
  return data;
};

export const deleteAutoResponseRuleApi = async (locationId: number, ruleId: string) => {
  if (USE_MOCK) return mocked(() => mockDeleteAutoResponseRule(ruleId), 400);
  const { data } = await visibilityApi.api.delete<UberallAutoResponseRuleListPayload>(
    locationPath(locationId, `/auto-response-rules/${ruleId}`),
  );
  return data;
};

export const getLocationProfileApi = async (locationId: number) => {
  if (USE_MOCK) return mocked(mockLocationProfile);
  const { data } = await visibilityApi.api.get<UberallLocationProfilePayload>(locationPath(locationId));
  return data;
};

export const patchLocationApi = async (locationId: number, patch: Record<string, unknown>) => {
  if (USE_MOCK) return mocked(() => mockPatchLocation(patch));
  const { data } = await visibilityApi.api.patch<UberallLocationProfilePayload>(locationPath(locationId), patch);
  return data;
};

const withJsonErrorBody = async (error: unknown): Promise<unknown> => {
  const response = (error as { response?: { data?: unknown } } | undefined)?.response;
  if (response?.data instanceof Blob) {
    try {
      response.data = JSON.parse(await response.data.text());
    } catch (_parseError) {
      response.data = undefined;
    }
  }
  return error;
};

const fetchPdf = async (request: () => Promise<{ data: Blob }>): Promise<Blob> => {
  try {
    const { data } = await request();
    return data;
  } catch (error: unknown) {
    throw await withJsonErrorBody(error);
  }
};

export const generateSeoReportApi = async (locationId: number): Promise<Blob> => {
  if (USE_MOCK) return mocked(mockGenerateSeoReport, 1200);
  return fetchPdf(() =>
    visibilityApi.api.post<Blob>(locationPath(locationId, '/seo-report'), undefined, { responseType: 'blob' }),
  );
};

export const downloadSeoReportApi = async (locationId: number): Promise<Blob> => {
  if (USE_MOCK) return mocked(mockDownloadSeoReport, 800);
  return fetchPdf(() => visibilityApi.api.get<Blob>(locationPath(locationId, '/seo-report'), { responseType: 'blob' }));
};

export const sendSeoReportApi = async (locationId: number) => {
  if (USE_MOCK) return mocked(mockSendSeoReport, 900);
  const { data } = await visibilityApi.api.post<UberallSeoReportSentPayload>(
    locationPath(locationId, '/seo-report/send'),
  );
  return data;
};

export const getListingsApi = async (locationId: number) => {
  if (USE_MOCK) return mocked(mockListings);
  const { data } = await visibilityApi.api.get<UberallListingsSummaryPayload>(locationPath(locationId, '/listings'));
  return data;
};

export const syncListingsApi = async (locationId: number) => {
  if (USE_MOCK) return mocked(mockSyncListings, 900);
  const { data } = await visibilityApi.api.post<UberallListingsSummaryPayload>(locationPath(locationId, '/sync'), {});
  return data;
};

export const getPhotosApi = async (locationId: number) => {
  if (USE_MOCK) return mocked(mockPhotos);
  const { data } = await visibilityApi.api.get<UberallPhotosPayload>(locationPath(locationId, '/photos'));
  return data;
};

export const uploadPhotoApi = async (locationId: number, slot: UberallPhotoSlot, file: File) => {
  if (USE_MOCK) return mocked(() => mockUploadPhoto(slot, file), 700);
  const form = new FormData();
  form.append('fileb', file);
  const { data } = await visibilityApi.api.post<UberallPhotosPayload>(locationPath(locationId, '/photos'), form, {
    params: { slot },
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const deletePhotoApi = async (locationId: number, photoId: number) => {
  if (USE_MOCK) return mocked(() => mockDeletePhoto(photoId));
  const { data } = await visibilityApi.api.delete<UberallPhotosPayload>(locationPath(locationId, `/photos/${photoId}`));
  return data;
};

export const getKeywordsApi = async (locationId: number) => {
  if (USE_MOCK) return mocked(mockKeywords);
  const { data } = await visibilityApi.api.get<UberallKeywordsPayload>(locationPath(locationId, '/keywords'));
  return data;
};

export const putKeywordsApi = async (locationId: number, keywords: string[]) => {
  if (USE_MOCK) return mocked(() => mockPutKeywords(keywords));
  const { data } = await visibilityApi.api.put<UberallKeywordsPayload>(locationPath(locationId, '/keywords'), {
    keywords,
  });
  return data;
};

export interface ComparisonWindow {
  compareStartDate: string;
  compareEndDate: string;
}

export const getAnalyticsApi = async (
  locationId: number,
  startDate: string,
  endDate: string,
  comparison: ComparisonWindow | null = null,
) => {
  if (USE_MOCK) return mocked(() => mockAnalytics(startDate, endDate));
  const { data } = await visibilityApi.api.get<UberallAnalyticsPayload>(locationPath(locationId, '/analytics'), {
    params: { startDate, endDate, ...comparison },
  });
  return data;
};

/** Slow on a cold cache: Uberall's ranking chart can take twenty seconds to answer. */
export const getKeywordRankingsApi = async (locationId: number) => {
  if (USE_MOCK) return mocked(mockKeywordRankings, 800);
  const { data } = await visibilityApi.api.get<KeywordRankingsPayload>(locationPath(locationId, '/keyword-rankings'));
  return data;
};

export const getScoreApi = async (locationId: number) => {
  if (USE_MOCK) return mocked(mockScore);
  const { data } = await visibilityApi.api.get<UberallScorePayload>(locationPath(locationId, '/score'));
  return data;
};

export interface DatapointsQuery {
  answered?: boolean | null;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export const getDatapointsApi = async (locationId: number, query: DatapointsQuery = {}) => {
  if (USE_MOCK) return mocked(() => mockDatapoints(query));
  const { data } = await visibilityApi.api.get<UberallDataPointsPagePayload>(locationPath(locationId, '/datapoints'), {
    params: { ...query, answered: query.answered ?? undefined },
  });
  return data;
};

export const getDatapointApi = async (datapointId: number) => {
  if (USE_MOCK) return mocked(() => mockDatapoint(datapointId));
  const { data } = await visibilityApi.api.get<UberallDataPointPayload>(datapointPath(datapointId));
  return data;
};

export const markDatapointReadApi = async (datapointId: number) => {
  if (USE_MOCK) return mocked(() => mockMarkDatapointRead(datapointId));
  const { data } = await visibilityApi.api.post<UberallDataPointPayload>(datapointPath(datapointId, '/read'));
  return data;
};

export const replyToDatapointApi = async (datapointId: number, text: string) => {
  if (USE_MOCK) return mocked(() => mockReplyToDatapoint(datapointId, text), 500);
  const { data } = await visibilityApi.api.post<UberallDataPointPayload>(datapointPath(datapointId, '/reply'), {
    text,
  });
  return data;
};

export const generateAiReplyApi = async (datapointId: number, instruction?: string) => {
  if (USE_MOCK) return mocked(() => mockAiReply(datapointId, instruction), 900);
  const { data } = await visibilityApi.api.post<UberallAiReplyPayload>(
    datapointPath(datapointId, '/ai-reply'),
    instruction ? { instruction } : {},
  );
  return data;
};

export const generateAiPostApi = async (locationId: number, body: UberallAiPostBody) => {
  if (USE_MOCK) return mocked(() => mockAiPost(locationId, body), 1400);
  const { data } = await visibilityApi.api.post<UberallAiPostPayload>(locationPath(locationId, '/ai-post'), body);
  return data;
};

export const getPostDirectoriesApi = async (locationId: number) => {
  if (USE_MOCK) return mocked(mockPostDirectories);
  const { data } = await visibilityApi.api.get<UberallPostDirectoriesPayload>(
    locationPath(locationId, '/posts/directories'),
  );
  return data;
};

export const getPostsApi = async (locationId: number) => {
  if (USE_MOCK) return mocked(mockPosts);
  const { data } = await visibilityApi.api.get<UberallSocialPostListPayload>(locationPath(locationId, '/posts'));
  return data;
};

/**
 * Uploads the post's visual and answers the URL to name in the post body. Uberall takes a post
 * image as a link it fetches, not as multipart, so this is a separate call that creates nothing and
 * touches no post. The form field is `fileb` — the backend reads that name and no other.
 */
export const uploadPostVisualApi = async (locationId: number, file: File): Promise<string> => {
  if (USE_MOCK) return mocked(() => mockUploadPostVisual(file));

  const form = new FormData();
  form.append('fileb', file);

  const { data } = await visibilityApi.api.post<{ url: string }>(locationPath(locationId, '/post-visuals'), form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data.url;
};

export const createPostApi = async (locationId: number, body: UberallSocialPostBody) => {
  if (USE_MOCK) return mocked(() => mockCreatePost(body), 500);
  const { data } = await visibilityApi.api.post<UberallSocialPostListPayload>(locationPath(locationId, '/posts'), body);
  return data;
};

export const updatePostApi = async (locationId: number, postId: number, body: UberallSocialPostBody) => {
  if (USE_MOCK) return mocked(() => mockUpdatePost(postId, body), 500);
  const { data } = await visibilityApi.api.patch<UberallSocialPostListPayload>(
    locationPath(locationId, `/posts/${postId}`),
    body,
  );
  return data;
};

export const deletePostApi = async (locationId: number, postId: number) => {
  if (USE_MOCK) return mocked(() => mockDeletePost(postId));
  const { data } = await visibilityApi.api.delete<UberallSocialPostListPayload>(
    locationPath(locationId, `/posts/${postId}`),
  );
  return data;
};

export const getSocialConnectionsApi = async (locationId: number) => {
  if (USE_MOCK) return mocked(mockSocialConnections);
  const { data } = await visibilityApi.api.get<UberallSocialConnectionsPayload>(
    locationPath(locationId, '/social-connections'),
  );
  return data;
};

/** Instagram has a hosted flow of its own, open once Facebook is connected. */
export const getSocialConnectUrlApi = async (locationId: number, platform: UberallSocialPlatform, language: string) => {
  if (USE_MOCK) return mocked(() => mockSocialConnectUrl(platform)).then(({ url }) => url);
  const { data } = await visibilityApi.api.get<{ url: string }>(
    locationPath(locationId, `/social-connections/${platform.toLowerCase()}/connect-url`),
    { params: { language } },
  );
  return data.url;
};

export const disconnectSocialApi = async (locationId: number, platform: UberallSocialPlatform) => {
  if (USE_MOCK) return mocked(() => mockDisconnectSocial(platform));
  const { data } = await visibilityApi.api.delete<UberallSocialConnectionsPayload>(
    locationPath(locationId, `/social-connections/${platform.toLowerCase()}`),
  );
  return data;
};
